import { useCallback, useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { IRequest } from "../interfaces/request";
import { IUser, IUserRequest } from "../interfaces/user";
import { loginReq, recoverDataUser, recoverPassReq } from "../request/login";
import { isExpired, decodeToken } from "react-jwt";
import { userContext } from "../context/UserContext/userContext";
import { showPopup } from "../utils/showAlert";
import { showLoader } from "../utils/loader";

import useMixpanel from 'hooks/useMixpanel/useMixpanel';



export const useLogin = () => {
    const navigate = useNavigate();
    const [error, setError] = useState<string>();
    const [loading, setLoading] = useState<boolean>(false);
    const context = useContext(userContext);
    const { userState, setUser, setToken } = context;
    const { user } = userState;
    const location = useLocation();
    const { track } = useMixpanel();

    useEffect(() => {
        if (loading) {
            showLoader();
        }
        else {
            Swal.close();
        }
    }, [loading])

    const login = useCallback(async (email?: string, clave?: string) => {
        setLoading(true);
        try {
            if (!email || !clave) {
                const token = localStorage.getItem("token");
                if (token) {
                    const decoded: any = decodeToken(token);
                    if (!isExpired(token) && decoded?.data) {
                        if (!userState?.user) {
                            const user = await recoverDataUser();
                            setUser(user);
                            setToken(token);
                        }
                        if (location.pathname === "/inicio") {
                            return navigate("/");
                        }
                        return;
                    }
                    track("Token expirado");
                    showPopup({
                        message: "Su sesión ha expirado, por favor inicie sesión nuevamente",
                        title: "Sesión expirada",
                        icon: "error"
                    });
                }
                return navigate("/inicio");
            }
            const res: IUserRequest = await loginReq(email, clave);
            if (res.status === "200") {
                setError(undefined);
                let usuario: IUser;
                res.data?.token && localStorage.setItem("token", res.data.token);
                if (res.data?.token) {
                    const decode: any = decodeToken(res.data.token);
                    let { data }: { data: IUser } = decode;
                    usuario = data;
                    setUser(usuario);
                    setToken(res.data.token);
                    track("Iniciar sesion exitoso");
                }
                navigate("/comandas");
            } else {
                setError(res.message);
                track("Iniciar sesion fallido", { data: res.message });
                await showPopup({
                    title: "No pudimos iniciar tu sesión",
                    message: res.message || "Revisa tu correo y contraseña, e intenta nuevamente.",
                    icon: "error"
                });
            }
        } catch (error: any) {
            console.log(error);
            track("Iniciar sesion fallido", error?.message);
            // BUG previo: la expresión `(error.message && error.message !== "Failed to fetch") || fallback`
            // devolvía el boolean `true` cuando error.message era cualquier string distinto a "Failed to fetch",
            // mostrando el modal con texto literal "true". Reemplazado por un ternario explícito.
            const isOffline =
                !error?.message ||
                error.message === "Failed to fetch" ||
                error.message === "timeout";
            const friendlyMessage = isOffline
                ? "Parece que no tienes conexión a internet. Verifica tu conexión y vuelve a intentar."
                : error.message;
            await showPopup({
                title: "No pudimos iniciar tu sesión",
                message: friendlyMessage,
                icon: "error",
                confirmButtonText: "Reintentar"
            });
            // NOTA: ya no recargamos automáticamente — el reload constante causaba loop
            // infinito cuando el problema persistía (ver caso SOHO Schell 2026-05-20).
            // El usuario decide si recargar (botón "Reintentar" cierra el popup; si quiere
            // reintentar, vuelve a tocar "Ingresar").
        } finally {
            setLoading(false);
            Swal.close();
        }
    }, [navigate]);// eslint-disable-line

    const logout = () => {
        track("Cerrar sesion");
        // F7: limpiar token y redirigir explícitamente a /inicio en vez de reload,
        // para garantizar que el usuario quede en la pantalla de login.
        // (Socket.IO ya fue removido del proyecto, ver commit 2d212aa).
        localStorage.removeItem("token");
        setUser(undefined);
        setToken(undefined);
        window.location.href = "/inicio";
    }



    const recoverPass = async (email: string) => {
        setLoading(true);
        try {
            const res: IRequest = await recoverPassReq(email);
            setLoading(false);
            if (res.status === "200") {
                track("Recuperación de contraseña exitosa");
                setTimeout(() => {
                    showPopup({
                        message: "Hemos enviado un correo para la recuperación de la cuenta",
                        icon: "success",
                        confirmButtonText: "Aceptar",
                    })
                }, 300);
            } else {
                track("Recuperación de contraseña fallida", { data: res.message });
                setTimeout(() => {
                    showPopup({
                        message: `${res.message}`,
                        icon: "error"
                    })
                }, 300);
            }
        } catch (error) {
            track("Recuperación de contraseña fallida", { data: error });
            setTimeout(() => {
                showPopup({
                    message: `${'Ocurrio un error'}`,
                    icon: "error"
                })
            }, 300);
            return
        } finally {
            setLoading(false);
        }
    }

    return {
        user,
        error,
        loading,
        recoverPass,
        login,
        logout
    }
}